import React, { useState, useEffect } from "react";
import {
    BasePageLayout,
    BaseBox,
    BaseTypography,
} from "../../Common/UI/exports";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";

import { fetchRoles, addRole, updateRole, deleteRole, getUsageCountForRole } from "../../APIs/roleManagementAPI";

// Simple in-memory cache
const cache = {
    roles: null,
};

function RoleManagement() {
    const [roles, setRoles] = useState([]);
    const [filterText, setFilterText] = useState("");

    // Dialog state (reused for create + edit)
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState("create"); // 'create' | 'edit'
    const [selectedRole, setSelectedRole] = useState(null); // role being edited
    const [roleNameInput, setRoleNameInput] = useState("");
    const [saving, setSaving] = useState(false);

    // Delete dialog state
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState(null);
    const [roleUsageCount, setRoleUsageCount] = useState(0);
    const [deleting, setDeleting] = useState(false);

    const handleFilterChange = (e) => setFilterText(e.target.value);

    // Normalize roles to ensure { id?, name } shape
    const normalizeRole = (r) => {
        if (!r || typeof r !== "object") return { id: undefined, name: String(r ?? "") };
        return {
            ...r,
            name: r.name ?? r.roleName ?? "",
        };
    };

    // Open dialog in Create mode
    const handleOpenCreate = () => {
        setDialogMode("create");
        setSelectedRole(null);
        setRoleNameInput("");
        setIsDialogOpen(true);
    };

    // Open dialog in Edit mode
    const handleOpenEdit = (role) => {
        const normalized = normalizeRole(role);
        setDialogMode("edit");
        setSelectedRole(normalized);
        setRoleNameInput(normalized.name || "");
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        if (saving) return;
        setIsDialogOpen(false);
        setSelectedRole(null);
        setRoleNameInput("");
        setDialogMode("create");
    };

    // Prevent duplicates by case-insensitive name match
    const nameExists = (name, ignoreId) => {
        const n = name.trim().toLowerCase();
        return roles.some((r) => {
            const rr = normalizeRole(r);
            if (ignoreId && rr.id === ignoreId) return false;
            return (rr.name || "").toLowerCase() === n;
        });
    };

    // Save handler (create or edit)
    const handleSave = async () => {
        const name = roleNameInput.trim();
        if (!name) {
            toast.warn("Please enter a role name.");
            return;
        }

        if (dialogMode === "create" && nameExists(name)) {
            toast.warn(`Role "${name}" already exists.`);
            return;
        }

        if (dialogMode === "edit") {
            const ignoreId = selectedRole?.id;
            if (nameExists(name, ignoreId)) {
                toast.warn(`Role "${name}" already exists.`);
                return;
            }
        }

        try {
            setSaving(true);

            if (dialogMode === "create") {
                const created = await addRole(name);
                const createdList = Array.isArray(created) ? created : [created];
                const normalizedCreated = createdList.map(normalizeRole);

                const updatedRoles = [...roles, ...normalizedCreated];
                setRoles(updatedRoles);
                cache.roles = updatedRoles;
                toast.success(`Role "${name}" created successfully.`);
                handleCloseDialog();
            } else {
                // edit mode
                const updated = await updateRole(selectedRole.id, name);
                const normalizedUpdated = normalizeRole(updated);

                // Fallback: if API doesn't return the updated entity,
                // synthesize with local selectedRole and new name
                const finalUpdated =
                    normalizedUpdated && normalizedUpdated.name
                        ? normalizedUpdated
                        : { ...selectedRole, name };

                const updatedRoles = roles.map((r) => {
                    const rr = normalizeRole(r);
                    if (rr.id != null && finalUpdated.id != null) {
                        return rr.id === finalUpdated.id ? finalUpdated : rr;
                    }
                    // If no ids, match by old name as a fallback (not ideal)
                    return rr.name === selectedRole.name ? finalUpdated : rr;
                });

                setRoles(updatedRoles);
                cache.roles = updatedRoles;
                toast.success(`Role renamed to "${name}" successfully.`);
                handleCloseDialog();
            }
        } catch (error) {
            console.error("Error saving role:", error);
            toast.error(
                dialogMode === "create" ? "Failed to add new role." : "Failed to update role."
            );
        } finally {
            setSaving(false);
        }
    };

    const filteredRoles = roles
        .map(normalizeRole)
        .filter((role) => role.name.toLowerCase().includes(filterText.toLowerCase()));

    useEffect(() => {
        const loadRoles = async () => {
            if (cache.roles) {
                setRoles(cache.roles.map(normalizeRole));
            } else {
                try {
                    const data = await fetchRoles();
                    const normalized = Array.isArray(data) ? data.map(normalizeRole) : [];
                    setRoles(normalized);
                    cache.roles = normalized;
                } catch (error) {
                    console.error("Error loading Roles:", error);
                    toast.error("Failed to load Roles.");
                }
            }
        };
        loadRoles();
    }, []);

    // ---------- DELETE FLOW ----------
    const handleOpenDelete = async (role) => {
        const normalized = normalizeRole(role);
        setRoleToDelete(normalized);

        const count = await getUsageCountForRole(normalized.name);
        setRoleUsageCount(count);

        setIsDeleteOpen(true);
    };

    const handleCloseDelete = () => {
        if (deleting) return;
        setIsDeleteOpen(false);
        setRoleToDelete(null);
        setRoleUsageCount(0);
    };

    const handleConfirmDelete = async () => {
        if (!roleToDelete) return;

        try {
            setDeleting(true);

            // Prefer deleting by id; fallback to name if your API uses name
            await deleteRole(roleToDelete.id ?? roleToDelete.name);

            // Remove from state
            setRoles((prev) => {
                const updated = prev.filter((r) => {
                    const rr = normalizeRole(r);
                    const key = rr.id ?? rr.name;
                    const delKey = roleToDelete.id ?? roleToDelete.name;
                    return key !== delKey;
                });
                cache.roles = updated;
                return updated;
            });

            toast.success(`Role "${roleToDelete.name}" deleted successfully.`);
            handleCloseDelete();
        } catch (error) {
            const serverMsg =
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.response?.data ||
                error.message;

            if (error?.response?.status === 409) {
                // Common when role is still assigned to users
                toast.error(
                    serverMsg ||
                    `Cannot delete "${roleToDelete.name}" because it is assigned to users. Remove it from users first.`
                );
            } else {
                toast.error(serverMsg || "Failed to delete role. Please try again.");
            }
            console.error("Delete role error:", error);
        } finally {
            setDeleting(false);
        }
    };

    // ---------- /DELETE FLOW ----------

    return (
        <BasePageLayout sidebarItems={[]}>
            <ToastContainer position="top-right" autoClose={3000} />
            <BaseBox
                sx={{
                    height: "calc(100vh - 140px)",
                    display: "flex",
                    flexDirection: "column",
                    maxWidth: "1160px",
                    mx: "auto",
                    mt: 0,
                    px: 2,
                }}
            >
                <BaseTypography sx={{ fontSize: "1.2rem", mb: 2, fontWeight: 600 }}>
                    All Roles
                </BaseTypography>

                {/* Filter & Create */}

                <BaseBox
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        mb: 3,
                    }}
                >
                    <BaseBox
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 2,
                            width: "100%",
                        }}
                    >
                        <TextField
                            size="small"
                            placeholder="Filter Roles..."
                            value={filterText}
                            onChange={handleFilterChange}
                            sx={{
                                minWidth: 300,
                                "& .MuiInputBase-root": {
                                    height: 32,         // ✅ match button height if needed
                                },
                            }}
                        />

                        <Button
                            variant="contained"
                            color="error"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={handleOpenCreate}
                            sx={{ whiteSpace: "nowrap", height: 32, minHeight: 32 }}
                        >
                            Create New Role
                        </Button>
                    </BaseBox>
                </BaseBox>

                {/* Roles Table */}
                <BaseBox
                    sx={{
                        border: "1px solid #ccc",
                        borderRadius: "6px",
                        backgroundColor: "#fff",
                        height: "auto",
                        alignSelf: "flex-start",
                        maxHeight: "400px",
                        overflowY: "auto",
                    }}
                >
                    <table className="users-roles-table">
                        <thead>
                            <tr>
                                <th>Role</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredRoles.length ? (
                                filteredRoles.map((role, idx) => {
                                    const roleName = role?.name ?? "";
                                    const rowKey = role?.id ?? `${roleName}-${idx}`;
                                    return (
                                        <tr key={rowKey}>
                                            <td className="action-col center" title={roleName}>{roleName}</td>
                                            <td className="action-col center">
                                                <Tooltip title="Edit role">
                                                    <IconButton
                                                        color="primary"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenEdit(role);
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>

                                                <Tooltip title="Delete role">
                                                    <IconButton
                                                        color="error"
                                                        size="small"
                                                        sx={{ ml: 1.5 }} // space between icons
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleOpenDelete(role); // <-- opens confirmation popup
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={2} className="empty">
                                        No Roles match your filter.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </BaseBox>
            </BaseBox>

            {/* Create / Edit Role Dialog (reused) */}
            <Dialog open={isDialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="xs">
                <DialogTitle>
                    {dialogMode === "create" ? "Create New Role" : "Update Role Name"}
                </DialogTitle>
                <DialogContent dividers>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Role Name"
                        placeholder="Enter role name"
                        value={roleNameInput}
                        onChange={(e) => setRoleNameInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                handleSave();
                            }
                        }}
                        disabled={saving}
                        margin="dense"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={saving} variant="outlined">
                        Close
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={!roleNameInput.trim() || saving}
                        variant="contained"
                    >
                        {saving ? "Saving..." : dialogMode === "create" ? "Save" : "Update"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Role Confirmation Dialog */}
            <Dialog open={isDeleteOpen} onClose={handleCloseDelete} fullWidth maxWidth="xs">
                <DialogTitle>Delete Role</DialogTitle>
                <DialogContent dividers>
                    <BaseTypography sx={{ mb: 1 }}>
                        You’re about to delete the role <b>{roleToDelete?.name}</b>.
                    </BaseTypography>

                    <BaseTypography>
                        {roleUsageCount > 0 ? (
                            <>
                                There {roleUsageCount === 1 ? "is" : "are"} <b>{roleUsageCount}</b>{" "}
                                user{roleUsageCount > 1 ? "s" : ""} who currently have this role.
                            </>
                        ) : (
                            <>No users are currently assigned to this role.</>
                        )}
                    </BaseTypography>

                    <BaseTypography sx={{ mt: 2, color: "error.main" }}>
                        This action is <b>irreversible</b>. Users assigned to this role will permanently lose all associated permissions.
                    </BaseTypography>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={handleCloseDelete} disabled={deleting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmDelete}
                        disabled={deleting}
                    >
                        {deleting ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>
        </BasePageLayout>
    );
}

export default RoleManagement;